import { useRef, useState } from "react";
import { Play } from "lucide-react";
import { setHeading, setText } from "../constants/typography";
import { CONTENT } from "../constants/content";
import Reveal from "./ui/Reveal";
import CtaButton from "./ui/CtaButton";
import styles from "../styles/Testimonials.module.scss";

const Testimonials = () => {
  const { title, subtitle, videos, screenshots, cta } = CONTENT.testimonials;

  const videoRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(null);
  /*
    A clip only mounts its <video> once the visitor asks for it. Until then
    the poster is a lazily-loaded <img>, which keeps ~3 MB of poster art and
    all video metadata off the initial page load.
  */
  const [startedIndexes, setStartedIndexes] = useState(() => new Set());

  const startVideo = (index) =>
    setStartedIndexes((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });

  const playVideo = (index) => {
    const videoElement = videoRefs.current[index];
    if (!videoElement) {
      // Not mounted yet — mounting it with autoPlay starts playback.
      startVideo(index);
      return;
    }

    const playPromise = videoElement.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.error("Error attempting to play video:", error);
      });
    }
  };

  const handleVideoClick = (event, index) => {
    // Keep the original click-to-toggle behaviour on the video surface.
    event.preventDefault();
    const videoElement = videoRefs.current[index];
    if (!videoElement) return;

    if (videoElement.paused) {
      playVideo(index);
    } else {
      videoElement.pause();
    }
  };

  return (
    <section
      id="testimonials"
      className={styles.testimonials}
      aria-label="Testimonianze degli studenti"
    >
      <div className={styles.header}>
        <Reveal>
          <span className={styles.eyebrow}>Testimonianze</span>
          <h2 className={styles.title}>{setHeading(title)}</h2>
        </Reveal>
      </div>

      <div className={styles.videoGrid}>
        {videos.map((video, index) => {
          const hasStarted = startedIndexes.has(index);

          return (
            <Reveal
              key={video.studentName}
              className={styles.videoItem}
              delay={index * 0.1}
              amount={0.2}
            >
              <div
                className={`${styles.videoFrame} ${
                  activeIndex === index ? styles.isPlaying : ""
                }`}
              >
                {hasStarted ? (
                  <video
                    className={styles.videoPlayer}
                    controls
                    autoPlay
                    poster={video.posterUrl}
                    src={video.videoUrl}
                    preload="metadata"
                    playsInline
                    ref={(el) => (videoRefs.current[index] = el)}
                    onClick={(e) => handleVideoClick(e, index)}
                    onPlay={() => {
                      setActiveIndex(index);
                      // Only one testimonial plays at a time.
                      videoRefs.current.forEach((el, i) => {
                        if (el && i !== index && !el.paused) el.pause();
                      });
                    }}
                    onPause={() => setActiveIndex(null)}
                    onEnded={() => setActiveIndex(null)}
                    aria-label={`Videotestimonianza di ${video.studentName}`}
                  />
                ) : (
                  <>
                    <img
                      className={styles.videoPlayer}
                      src={video.posterUrl}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                    />
                    <button
                      type="button"
                      className={styles.playButton}
                      onClick={() => startVideo(index)}
                      aria-label={`Riproduci la videotestimonianza di ${video.studentName}`}
                    >
                      <Play aria-hidden="true" />
                    </button>
                  </>
                )}
              </div>

              <figure className={styles.quote}>
                <blockquote>
                  <p>{setText(video.description)}</p>
                </blockquote>
                <figcaption className={styles.studentName}>
                  {video.studentName}
                </figcaption>
              </figure>
            </Reveal>
          );
        })}
      </div>

      <div className={styles.screenshotsHeader}>
        <Reveal as="h3" className={styles.screenshotsSubtitle}>
          {setHeading(subtitle)}
        </Reveal>
      </div>

      <div className={styles.screenshotsGrid}>
        {screenshots.map((ss, index) => (
          <Reveal
            key={index}
            className={styles.screenshotCard}
            delay={index * 0.08}
            amount={0.15}
          >
            <img
              src={ss.imageUrl}
              alt={ss.alt}
              width={ss.width}
              height={ss.height}
              loading="lazy"
              decoding="async"
            />
          </Reveal>
        ))}
      </div>

      <Reveal className={styles.ctaWrapper}>
        <CtaButton to="register">{cta}</CtaButton>
      </Reveal>
    </section>
  );
};

export default Testimonials;
